import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { startLoadingFlowAssignmentPolicies } from "../../app/actions/flowAssignmentPolicyActions";
import { listFlowCoverageSnapshot } from "../../app/actions/flowCoverageActions";
import { startLoadingFlowRoleUsers } from "../../app/actions/flowModuleConfigActions";
import {
  DIAGRAM_CHAIN_ROLES,
  DIAGRAM_HOPS,
  DIAGRAM_ISOLATED_ROLES,
  buildDisplayedRules,
  collectCoverageUsers,
  collectFallbackUsers,
  findHopPolicy,
  formatDiagramUser
} from "./flowModuleDiagramHelper";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "720px",
    width: "90%",
    maxHeight: "80vh",
    overflow: "auto",
    padding: 0
  }
};

if (typeof document !== "undefined") {
  Modal.setAppElement("#root");
}

const FlowModuleConfigDiagramPanel = ({
  catalogmoduleId,
  roleUsers = {}
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { policies, loading: policiesLoading } = useSelector((state) => state.flowAssignmentPolicy);
  const [coverageByRole, setCoverageByRole] = useState({});
  const [isolatedUsers, setIsolatedUsers] = useState({ liaison: [], coordinator: [] });
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    if (!catalogmoduleId) {
      return;
    }
    dispatch(startLoadingFlowAssignmentPolicies({ catalogmodule_id: catalogmoduleId }));
  }, [catalogmoduleId, dispatch]);

  useEffect(() => {
    let cancelled = false;
    if (!catalogmoduleId) {
      setCoverageByRole({});
      setIsolatedUsers({ liaison: [], coordinator: [] });
      return undefined;
    }

    const load = async () => {
      setCoverageLoading(true);
      try {
        const [feeder, pre, manager, validator, editor, liaison, coordinator] = await Promise.all([
          listFlowCoverageSnapshot({ catalogmodule_id: catalogmoduleId, role: "feeder" }),
          listFlowCoverageSnapshot({ catalogmodule_id: catalogmoduleId, role: "pre-manager" }),
          listFlowCoverageSnapshot({ catalogmodule_id: catalogmoduleId, role: "manager" }),
          listFlowCoverageSnapshot({ catalogmodule_id: catalogmoduleId, role: "validator" }),
          listFlowCoverageSnapshot({ catalogmodule_id: catalogmoduleId, role: "editor" }),
          dispatch(startLoadingFlowRoleUsers("liaison", catalogmoduleId)),
          dispatch(startLoadingFlowRoleUsers("coordinator", catalogmoduleId))
        ]);
        if (!cancelled) {
          setCoverageByRole({
            feeder,
            "pre-manager": pre,
            manager,
            validator,
            editor
          });
          setIsolatedUsers({
            liaison: liaison || [],
            coordinator: coordinator || []
          });
        }
      } finally {
        if (!cancelled) {
          setCoverageLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [catalogmoduleId, dispatch]);

  const hops = useMemo(() => (
    DIAGRAM_HOPS.map((hop) => {
      const sendPolicy = findHopPolicy(policies, catalogmoduleId, hop.from, hop.to, "send");
      const returnPolicy = findHopPolicy(policies, catalogmoduleId, hop.to, hop.from, "return");
      return {
        ...hop,
        send: {
          policy: sendPolicy,
          rules: buildDisplayedRules(sendPolicy),
          userRole: hop.to
        },
        return: {
          policy: returnPolicy,
          rules: buildDisplayedRules(returnPolicy),
          userRole: hop.from
        }
      };
    })
  ), [catalogmoduleId, policies]);

  const resolveRoleUsers = (roleKey) => {
    if (roleKey === "liaison" || roleKey === "coordinator") {
      return [{
        id: roleKey,
        name: "",
        members: collectFallbackUsers(isolatedUsers[roleKey] || [])
      }];
    }
    const fallbackKey = roleKey === "pre-manager" ? "preManager" : roleKey;
    const pool = roleUsers[fallbackKey] || [];
    const groups = collectCoverageUsers(coverageByRole[roleKey], pool);
    const hasMembers = groups.some((group) => group.members.length);
    if (hasMembers) {
      return groups;
    }
    return [{
      id: "fallback",
      name: "",
      members: collectFallbackUsers(pool)
    }];
  };

  const openUsersPopup = ({ roleKey, direction, title }) => {
    setPopup({
      roleKey,
      direction,
      title,
      groups: resolveRoleUsers(roleKey)
    });
  };

  const renderRuleList = (rules, hasPolicy) => (
    <ol className="flow-diagram__rules-list">
      {rules.map((rule, index) => {
        const code = rule.code || rule.rule_code || "";
        return (
          <li
            key={`${code || "rule"}-${rule.id || index}`}
            className={`flow-diagram__rule${rule.isWinner ? " is-winner" : ""}${rule.enabled === false ? " is-off" : ""}`}
          >
            {index + 1}. {t(`admin.flowModule.diagram.rule.${code}`, code || "—")}
            {rule.isWinner && (
              <span className="flow-diagram__winner-mark">
                {t("admin.flowModule.diagram.winner", "Actual")}
              </span>
            )}
          </li>
        );
      })}
      {!hasPolicy && (
        <li className="flow-diagram__rule is-off">
          {t("admin.flowModule.diagram.noPolicy", "Sin política")}
        </li>
      )}
    </ol>
  );

  const renderUsersButton = (roleKey, direction) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-dark flow-diagram__users-btn"
      onClick={() => openUsersPopup({
        roleKey,
        direction,
        title: direction
          ? `${t(`admin.flowModule.diagram.role.${roleKey}`, roleKey)} · ${t(`admin.flowModule.diagram.direction.${direction}`, direction)}`
          : t(`admin.flowModule.diagram.role.${roleKey}`, roleKey)
      })}
    >
      {t("admin.flowModule.diagram.viewUsers", "Ver usuarios")}
    </button>
  );

  if (!catalogmoduleId) {
    return (
      <div className="alert alert-warning">
        {t("admin.flowModule.diagram.selectModule", "Elija un módulo para ver el estado de la parametrización.")}
      </div>
    );
  }

  const popupGroups = popup?.groups || [];
  const popupHasGroups = popupGroups.some((group) => group.name);
  const popupHasUsers = popupGroups.some((group) => (group.members || []).length);

  return (
    <div className="flow-diagram">
      <p className="text-muted small">
        {t("admin.flowModule.diagram.hint", "Estado actual de cada tramo. El algoritmo marcado es el que gana hoy. Ver usuarios muestra el orden parametrizado de ida o regreso.")}
      </p>
      {(policiesLoading || coverageLoading) && (
        <p className="text-muted">{t("labels.loading", "Cargando...")}</p>
      )}

      <div className="flow-diagram__scroll">
        <div className="flow-diagram__grid">
          <div className="flow-diagram__cell" />
          {hops.map((hop) => (
            <React.Fragment key={`send-${hop.from}-${hop.to}`}>
              <div className="flow-diagram__cell flow-diagram__cell--rules">
                {renderRuleList(hop.send.rules, Boolean(hop.send.policy))}
                {renderUsersButton(hop.send.userRole, "send")}
                <div className="flow-diagram__label-row">
                  <span>{t("admin.flowModule.diagram.send", "Envía")}</span>
                  <span className="flow-diagram__arrow" aria-hidden="true">→</span>
                </div>
              </div>
              <div className="flow-diagram__cell" />
            </React.Fragment>
          ))}
          <div className="flow-diagram__cell flow-diagram__cell--approve-top" />

          {DIAGRAM_CHAIN_ROLES.map((role, index) => (
            <React.Fragment key={role.key}>
              <div className="flow-diagram__cell flow-diagram__cell--box">
                <div className={`flow-diagram__box flow-diagram__box--${role.color}`}>
                  {t(`admin.flowModule.diagram.role.${role.key}`, role.key)}
                </div>
              </div>
              {index < hops.length && <div className="flow-diagram__cell flow-diagram__cell--line" />}
            </React.Fragment>
          ))}
          <div className="flow-diagram__cell flow-diagram__cell--approve">
            <span>{t("admin.flowModule.diagram.approve", "Aprueba")}</span>
            <span className="flow-diagram__arrow" aria-hidden="true">→</span>
          </div>

          <div className="flow-diagram__cell" />
          {hops.map((hop) => (
            <React.Fragment key={`return-${hop.from}-${hop.to}`}>
              <div className="flow-diagram__cell flow-diagram__cell--rules">
                <div className="flow-diagram__label-row">
                  <span className="flow-diagram__arrow" aria-hidden="true">←</span>
                  <span>{t("admin.flowModule.diagram.return", "Regresa")}</span>
                </div>
                {renderRuleList(hop.return.rules, Boolean(hop.return.policy))}
                {renderUsersButton(hop.return.userRole, "return")}
              </div>
              <div className="flow-diagram__cell" />
            </React.Fragment>
          ))}
          <div className="flow-diagram__cell" />
        </div>
      </div>

      <div className="flow-diagram__isolated">
        <div className="flow-diagram__isolated-line" />
        <p className="text-muted small mb-3">
          {t("admin.flowModule.diagram.isolatedTitle", "Roles sin conexión en la cadena")}
        </p>
        <div className="flow-diagram__isolated-row">
          {DIAGRAM_ISOLATED_ROLES.map((role) => (
            <div className="flow-diagram__isolated-item" key={role.key}>
              <div className={`flow-diagram__box flow-diagram__box--${role.color}`}>
                {t(`admin.flowModule.diagram.role.${role.key}`, role.key)}
              </div>
              {renderUsersButton(role.key, "")}
            </div>
          ))}
        </div>
      </div>

      <Modal
        isOpen={Boolean(popup)}
        onRequestClose={() => setPopup(null)}
        style={modalStyles}
        overlayClassName="modal-fondo"
        contentLabel={t("admin.flowModule.diagram.usersTitle", "Usuarios")}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h5 className="mb-1">{t("admin.flowModule.diagram.usersTitle", "Usuarios")}</h5>
              {popup?.title && <p className="text-muted small mb-0">{popup.title}</p>}
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPopup(null)}>
              {t("buttons.close", "Cerrar")}
            </button>
          </div>
          {!popupHasUsers && (
            <p className="text-muted mb-0">
              {t("admin.flowModule.diagram.usersEmpty", "No hay usuarios parametrizados en este lugar.")}
            </p>
          )}
          {popupGroups.map((group) => (
            <div className="mb-3" key={group.id || group.name || "group"}>
              {popupHasGroups && group.name && (
                <h6 className="mb-2">{group.name}</h6>
              )}
              {(group.members || []).length > 0 && (
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th>{t("admin.flowModule.diagram.order", "Orden")}</th>
                      <th>{t("admin.flowModule.diagram.person", "Persona")}</th>
                      <th>{t("admin.flowModule.diagram.status", "Estado")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.members.map((member) => (
                      <tr key={member.id} className={member.active ? "" : "table-secondary text-muted"}>
                        <td>{member.order}</td>
                        <td>{formatDiagramUser(member.user)}</td>
                        <td>
                          {member.active
                            ? t("admin.flowModule.diagram.active", "Activo")
                            : t("admin.flowModule.diagram.inactive", "Inactivo")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

FlowModuleConfigDiagramPanel.propTypes = {
  catalogmoduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  roleUsers: PropTypes.shape({
    preManager: PropTypes.array,
    manager: PropTypes.array,
    validator: PropTypes.array,
    editor: PropTypes.array
  })
};

export default FlowModuleConfigDiagramPanel;
